// /profile — pilot profile with one verification card per airfield the
// pilot is a member of. Verification is per-(pilot, field): Harry signs
// off for HIS field, another field's admin does it independently.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Section, Cell, List, Button, Input, Caption, Spinner, Banner, Title,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const SKILL_OPTIONS = [
  { code: "beginner",     label: "Начинающий",  note: "Учусь, обычно с инструктором" },
  { code: "intermediate", label: "Средний",     note: "Уверенно летаю, освоил пилотаж" },
  { code: "experienced",  label: "Опытный",     note: "Соревнования, инструкторская практика" },
];

export default function Profile() {
  const navigate = useNavigate();
  usePageBack("/");
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api("/api/v1/me/profile")
      .then((r) => {
        setProfile(r.profile);
        setForm({
          last_name: r.profile.last_name || "",
          first_name: r.profile.first_name || "",
          middle_name: r.profile.middle_name || "",
          skill_class: r.profile.skill_class || "",
          fasr_id: r.profile.fasr_id || "",
          phone: r.profile.phone || "",
        });
      })
      .catch((e) => setError(`Не удалось загрузить профиль: ${e.message}`));
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const r = await api("/api/v1/me/profile", {
        method: "PATCH", body: JSON.stringify(form),
      });
      setProfile(r.profile);
      navigate(-1);
    } catch (e) {
      setError(e.message || "Не удалось сохранить.");
    } finally {
      setSaving(false);
    }
  }

  if (error && !profile) {
    return <List><Banner type="section" header="Ошибка">{error}</Banner></List>;
  }
  if (!profile) {
    return <List><Section><Cell before={<Spinner size="m" />}>Загружаем профиль…</Cell></Section></List>;
  }

  return (
    <List>
      <VerificationSection memberships={profile.memberships || []} />

      {error && <Banner type="section" header="Ошибка">{error}</Banner>}

      <Section header="Telegram">
        <Cell subtitle={profile.tg_username ? `@${profile.tg_username}` : `tg_user_id: ${profile.tg_user_id}`}>
          tg ID: {profile.tg_user_id}
        </Cell>
      </Section>

      <Section header="ФИО">
        <Cell>
          <Input
            header="Фамилия"
            placeholder="Иванов"
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
        </Cell>
        <Cell>
          <Input
            header="Имя"
            placeholder="Иван"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
        </Cell>
        <Cell>
          <Input
            header="Отчество"
            placeholder="Иванович"
            value={form.middle_name}
            onChange={(e) => setForm({ ...form, middle_name: e.target.value })}
          />
        </Cell>
      </Section>

      <Section header="Уровень">
        {SKILL_OPTIONS.map((s) => (
          <Cell
            key={s.code}
            onClick={() => setForm({ ...form, skill_class: form.skill_class === s.code ? "" : s.code })}
            subtitle={s.note}
            after={form.skill_class === s.code ? "✓" : ""}
          >
            {s.label}
          </Cell>
        ))}
        <Cell>
          <Caption level="1">
            Уровень нужен, чтобы другие пилоты видели общую раскладку («2 опытных, 2 средних»). Имена не показываются.
          </Caption>
        </Cell>
      </Section>

      <Section header="Контакты и документы">
        <Cell>
          <Input
            header="ID ФАСР Sport"
            placeholder="Только цифры/буквы карточки"
            value={form.fasr_id}
            onChange={(e) => setForm({ ...form, fasr_id: e.target.value })}
          />
        </Cell>
        <Cell>
          <Input
            header="Телефон"
            placeholder="+7 ..."
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Cell>
        <Cell>
          <Caption level="1">
            Эти данные не публичные — их видят только админ аэродрома и вы.
          </Caption>
        </Cell>
      </Section>

      <Section header="Сканы документов">
        <Cell
          onClick={() => navigate("/profile/documents")}
          subtitle="Загрузить фото ID ФАСР и страховки"
          after="→"
        >
          📷 Документы
        </Cell>
      </Section>

      <Section>
        <Cell>
          <Button stretched mode="filled" disabled={saving} onClick={save}>
            {saving ? "Сохраняем…" : "Сохранить"}
          </Button>
        </Cell>
        <Cell>
          <Button stretched mode="plain" disabled={saving} onClick={() => navigate("/")}>
            ← На главную
          </Button>
        </Cell>
      </Section>
    </List>
  );
}

function VerificationSection({ memberships }) {
  if (memberships.length === 0) {
    return (
      <Section>
        <Cell
          before={<div style={{ fontSize: 28 }}>⚪</div>}
          subtitle="Вы пока не состоите ни в одном аэродроме. Попросите админа добавить вас — после этого появится карточка подтверждения."
          multiline
        >
          <Title level="3">Нет аэродромов</Title>
        </Cell>
      </Section>
    );
  }
  return (
    <>
      {memberships.map((m) => (
        <VerificationCard key={m.airfield_slug} membership={m} />
      ))}
    </>
  );
}

function VerificationCard({ membership }) {
  const status = membership.verification_status || "unverified";
  const meta = STATUS_META[status] || STATUS_META.unverified;
  return (
    <Section header={`🛫 ${membership.airfield_name}`}>
      <Cell
        subtitle={meta.subtitle(membership)}
        before={<div style={{ fontSize: 28 }}>{meta.icon}</div>}
        after={<span className={`rcf-pill rcf-pill--${meta.tone}`}>{meta.pill}</span>}
        multiline
      >
        <Title level="3" style={{ color: meta.color }}>{meta.title}</Title>
      </Cell>
      {meta.body && <Cell multiline><Caption level="1">{meta.body(membership)}</Caption></Cell>}
    </Section>
  );
}

const STATUS_META = {
  unverified: {
    icon: "⚪",
    color: "#666",
    tone: "neutral",
    pill: "не подтверждён",
    title: "Не подтверждён",
    subtitle: () => "Заполните профиль и попросите админа подтвердить — пока можно осмотреться, но бронирование на этом аэродроме заблокировано.",
    body: null,
  },
  pending: {
    icon: "🟡",
    color: "#b07a00",
    tone: "warn",
    pill: "на проверке",
    title: "Документы на проверке",
    subtitle: () => "Обычно 1–2 дня. Можно осмотреться, но бронировать пока нельзя.",
    body: (m) => m.verification_note ? `Заметка: ${m.verification_note}` : null,
  },
  verified: {
    icon: "🟢",
    color: "var(--tg-theme-link-color, #007aff)",
    tone: "ok",
    pill: "подтверждён",
    title: "Подтверждён",
    subtitle: (m) => m.verified_until
      ? `Действует до ${formatDate(m.verified_until)}`
      : "Срок не указан",
    body: null,
  },
  rejected: {
    icon: "🔴",
    color: "#c80000",
    tone: "danger",
    pill: "отклонён",
    title: "Отклонён",
    subtitle: (m) => m.verification_note || "Свяжитесь с админом аэродрома, чтобы исправить.",
    body: () => "Перезагрузите документы, как только исправите причину.",
  },
};

function formatDate(iso) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}
